import React, { useEffect, useState } from 'react';
import api from '../api';
import { format } from 'date-fns';

const STATUS_LABELS = { pending:'Pending', in_progress:'In Progress', awaiting_info:'Awaiting Info', escalated:'Escalated', resolved:'Resolved', closed:'Closed' };
const PRIO_COLOR = { critical:'#D04040', high:'#E8A020', medium:'#1B8FD4', low:'#A0B8C8' };

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [workload, setWorkload] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/tickets/stats/dashboard'),
      api.get('/api/users/workload'),
      api.get('/api/tickets?limit=1000&page=1'),
    ]).then(([s, w, t]) => {
      setStats(s.data);
      setWorkload(w.data);
      setTickets(t.data.tickets || []);
    }).finally(() => setLoading(false));
  }, []);

  const exportTicketsCSV = () => {
    const BOM = '\uFEFF';
    const headers = [
      'Ticket No', 'Subject', 'Category', 'Branch', 'Priority', 'Status',
      'Created By', 'Assigned To', 'Date Created', 'Time Created',
      'SLA Deadline', 'Resolved At', 'SLA Breached'
    ];
    const rows = tickets.map(t => [
      t.ticket_number || '',
      t.subject || '',
      t.category || '',
      t.branch || '',
      t.priority || '',
      STATUS_LABELS[t.status] || t.status || '',
      t.created_by_name || '',
      t.assigned_to_name || 'Unassigned',
      t.created_at ? format(new Date(t.created_at), 'dd/MM/yyyy') : '',
      t.created_at ? format(new Date(t.created_at), 'HH:mm:ss') : '',
      t.sla_deadline ? format(new Date(t.sla_deadline), 'dd/MM/yyyy HH:mm') : '',
      t.resolved_at ? format(new Date(t.resolved_at), 'dd/MM/yyyy HH:mm') : '',
      t.sla_breached ? 'YES' : 'NO',
    ]);
    const csv = BOM + [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OMIYE-MFB-Tickets-KPI-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportKPICSV = () => {
    const BOM = '\uFEFF';
    const headers = ['Staff Name', 'Role', 'Branch', 'Active Tickets', 'Resolved Tickets', 'Overdue Tickets', 'Avg Resolution (hrs)', 'Workload %'];
    const rows = workload.map(s => [
      s.full_name,
      s.role ? s.role.replace(/_/g, ' ') : '',
      s.branch || '',
      s.active_tickets || 0,
      s.resolved_tickets || 0,
      s.overdue_tickets || 0,
      s.avg_resolution_hrs || 'N/A',
      Math.min(parseInt(s.active_tickets) * 15, 100) + '%',
    ]);
    const csv = BOM + [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OMIYE-MFB-Staff-KPI-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading">⏳ Loading reports…</div>;

  const totalTickets = (stats?.by_status || []).reduce((a, b) => a + parseInt(b.count), 0);
  const resolvedCount = parseInt((stats?.by_status || []).find(s => s.status === 'resolved')?.count || 0);
  const closedCount = parseInt((stats?.by_status || []).find(s => s.status === 'closed')?.count || 0);
  const resolutionRate = totalTickets ? Math.round(((resolvedCount + closedCount) / totalTickets) * 100) : 0;

  return (
    <div>
      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card stat-sky">
          <div className="stat-label">Total Tickets</div>
          <div className="stat-val">{totalTickets}</div>
          <div className="stat-sub">All time</div>
        </div>
        <div className="stat-card stat-green">
          <div className="stat-label">Resolution Rate</div>
          <div className="stat-val">{resolutionRate}%</div>
          <div className="stat-sub">Target: 90%</div>
        </div>
        <div className="stat-card stat-orange">
          <div className="stat-label">Open Tickets</div>
          <div className="stat-val">{stats?.open || 0}</div>
          <div className="stat-sub">Needs attention</div>
        </div>
        <div className="stat-card stat-red">
          <div className="stat-label">SLA Breached</div>
          <div className="stat-val">{stats?.sla_breached || 0}</div>
          <div className="stat-sub">{stats?.sla_breached > 0 ? '⚠️ Action needed' : '✅ All clear'}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
        {/* By Category */}
        <div className="card card-body">
          <div className="section-title" style={{ fontSize:12, marginBottom:12 }}>Tickets by Category</div>
          {(stats?.by_category || []).length === 0 && <div style={{ color:'var(--muted)', fontSize:12, textAlign:'center', padding:20 }}>No data yet</div>}
          {(stats?.by_category || []).map(c => {
            const pct = totalTickets ? Math.round((c.count / totalTickets) * 100) : 0;
            return (
              <div key={c.category} style={{ marginBottom:9 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:3 }}>
                  <span>{c.category}</span>
                  <span style={{ fontWeight:600 }}>{c.count} ({pct}%)</span>
                </div>
                <div className="sla-bar">
                  <div className="sla-fill" style={{ width:`${pct}%`, background:'var(--sky)' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* By Status */}
        <div className="card card-body">
          <div className="section-title" style={{ fontSize:12, marginBottom:12 }}>Tickets by Status</div>
          {(stats?.by_status || []).map(s => {
            const pct = totalTickets ? Math.round((s.count / totalTickets) * 100) : 0;
            return (
              <div key={s.status} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                <span className={`badge badge-${s.status}`} style={{ minWidth:100 }}>
                  {STATUS_LABELS[s.status] || s.status}
                </span>
                <div style={{ flex:1 }}>
                  <div className="sla-bar">
                    <div className="sla-fill" style={{ width:`${pct}%`, background:'var(--sky-mid)' }} />
                  </div>
                </div>
                <span style={{ fontSize:13, fontWeight:600, fontFamily:'Sora', minWidth:24, textAlign:'right' }}>{s.count}</span>
                <span style={{ fontSize:11, color:'var(--muted)', minWidth:34 }}>{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Staff KPI Table */}
      <div className="section-head" style={{ marginBottom:10 }}>
        <div className="section-title">👥 All Staff Performance</div>
        <button className="btn btn-success btn-sm" onClick={exportKPICSV}>📥 Export Staff KPI CSV</button>
      </div>
      <div className="table-wrap" style={{ marginBottom:16 }}>
        <table>
          <thead>
            <tr>
              <th>Staff Member</th>
              <th>Role</th>
              <th>Branch</th>
              <th>Active Tickets</th>
              <th>Resolved</th>
              <th>Overdue ⚠️</th>
              <th>Avg Resolution Time</th>
              <th>Workload</th>
            </tr>
          </thead>
          <tbody>
            {workload.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--muted)', padding:30 }}>No staff data</td></tr>
            )}
            {workload.map(s => {
              const load = Math.min(parseInt(s.active_tickets) * 15, 100);
              return (
                <tr key={s.id} style={{ cursor:'default' }}>
                  <td>
                    <div style={{ fontWeight:500 }}>{s.full_name}</div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>{s.email}</div>
                  </td>
                  <td style={{ fontSize:11, color:'var(--muted)', textTransform:'capitalize' }}>
                    {s.role ? s.role.replace(/_/g, ' ') : '—'}
                  </td>
                  <td style={{ fontSize:11, color:'var(--muted)' }}>{s.branch || '—'}</td>
                  <td style={{ fontFamily:'Sora', fontWeight:600, color:'var(--orange-dark)' }}>{s.active_tickets || 0}</td>
                  <td style={{ fontFamily:'Sora', fontWeight:600, color:'var(--success)' }}>{s.resolved_tickets || 0}</td>
                  <td style={{ fontFamily:'Sora', fontWeight:600, color: s.overdue_tickets > 0 ? 'var(--danger)' : 'var(--muted)' }}>{s.overdue_tickets || 0}</td>
                  <td style={{ color:'var(--muted)', fontSize:12 }}>{s.avg_resolution_hrs ? `${s.avg_resolution_hrs} hrs` : '—'}</td>
                  <td style={{ minWidth:120 }}>
                    <div style={{ fontSize:11, color:'var(--muted)', marginBottom:3 }}>{load}%</div>
                    <div className="sla-bar">
                      <div className="sla-fill" style={{ width:`${load}%`, background: load > 75 ? 'var(--warn)' : 'var(--sky)' }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* All Tickets Export */}
      <div className="section-head" style={{ marginBottom:10 }}>
        <div className="section-title">🎫 All Tickets Report</div>
        <button className="btn btn-success btn-sm" onClick={exportTicketsCSV}>📥 Export All Tickets CSV</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Ticket No</th><th>Subject</th><th>Category</th><th>Branch</th>
              <th>Priority</th><th>Status</th><th>Created By</th><th>Assigned To</th><th>Date</th><th>SLA</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 && (
              <tr><td colSpan={10} style={{ textAlign:'center', color:'var(--muted)', padding:30 }}>No tickets yet</td></tr>
            )}
            {tickets.slice(0,20).map(t => (
              <tr key={t.id} style={{ cursor:'default' }}>
                <td><span style={{ fontFamily:'Sora', fontSize:11, fontWeight:600, color:'var(--sky-dark)' }}>{t.ticket_number}</span></td>
                <td style={{ maxWidth:160 }}><div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:12 }}>{t.subject}</div></td>
                <td style={{ fontSize:11, color:'var(--muted)' }}>{t.category}</td>
                <td style={{ fontSize:11, color:'var(--muted)' }}>{t.branch}</td>
                <td><span className="prio-dot" style={{ background: PRIO_COLOR[t.priority] }} />{t.priority}</td>
                <td><span className={`badge badge-${t.status}`}>{STATUS_LABELS[t.status]}</span></td>
                <td style={{ fontSize:11 }}>{t.created_by_name || '—'}</td>
                <td style={{ fontSize:11 }}>{t.assigned_to_name || '—'}</td>
                <td style={{ fontSize:11, color:'var(--muted)', whiteSpace:'nowrap' }}>
                  {t.created_at ? format(new Date(t.created_at), 'dd MMM yyyy') : '—'}
                </td>
                <td>
                  {t.sla_breached
                    ? <span style={{ fontSize:10, color:'var(--danger)', fontWeight:600 }}>⚠️ Breached</span>
                    : <span style={{ fontSize:10, color:'var(--success)' }}>✅ OK</span>}
                </td>
              </tr>
            ))}
            {tickets.length > 20 && (
              <tr><td colSpan={10} style={{ textAlign:'center', color:'var(--muted)', fontSize:12, padding:12 }}>
                Showing 20 of {tickets.length} tickets. Export CSV to see all.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
